// ══════════════════════════════════════════════════════════════════════════════
// EMPLOI PRO - Offres d'emploi et candidatures pour professionnels
// Permet aux pros de poster des offres ET de chercher des emplois
// ══════════════════════════════════════════════════════════════════════════════

import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useOutletContext, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Briefcase, MapPin, Clock, Euro, Building2, Star, BadgeCheck,
  Search, Filter, ChevronDown, X, Plus, Send, Eye, Users,
  Zap, Calendar, CheckCircle, AlertCircle, FileText, User,
  TrendingUp, Heart, Bookmark, ExternalLink, PlusCircle,
  ArrowLeft, Sparkles, ChevronRight
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Types de contrats
const JOB_TYPES = [
  { id: 'all', label: 'Tous' },
  { id: 'cdi', label: 'CDI' },
  { id: 'cdd', label: 'CDD' },
  { id: 'interim', label: 'Intérim' },
  { id: 'freelance', label: 'Freelance' },
  { id: 'apprentissage', label: 'Apprentissage' },
  { id: 'stage', label: 'Stage' },
];

// Badges contrat
const JOB_TYPE_BADGES = {
  cdi: { label: 'CDI', bg: 'bg-green-100', text: 'text-green-700' },
  cdd: { label: 'CDD', bg: 'bg-blue-100', text: 'text-blue-700' },
  interim: { label: 'Intérim', bg: 'bg-purple-100', text: 'text-purple-700' },
  freelance: { label: 'Freelance', bg: 'bg-orange-100', text: 'text-orange-700' },
  apprentissage: { label: 'Apprentissage', bg: 'bg-cyan-100', text: 'text-cyan-700' },
  stage: { label: 'Stage', bg: 'bg-amber-100', text: 'text-amber-700' },
};

// Métiers
const METIERS = [
  'Plombier', 'Électricien', 'Maçon', 'Carreleur', 'Peintre', 'Menuisier',
  'Couvreur', 'Chauffagiste', 'Climaticien', 'Façadier', 'Charpentier',
  'Serrurier', 'Vitrier', 'Paysagiste', 'Pisciniste'
];

// Card offre d'emploi
function JobCard({ job, onApply, onSave, isSaved }) {
  const badge = JOB_TYPE_BADGES[job.job_type] || JOB_TYPE_BADGES.cdi;
  const [saved, setSaved] = useState(isSaved);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden hover:shadow-md transition-shadow"
    >
      <div className="p-5">
        {/* Header */}
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-start gap-3">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#1E3A5F] to-[#2d5a8a] flex items-center justify-center text-white font-bold text-sm flex-shrink-0">
              {job.company?.name?.slice(0, 2).toUpperCase() || 'E'}
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 text-lg">{job.title}</h3>
              <div className="flex items-center gap-2 text-sm text-gray-500">
                <span>{job.company?.name}</span>
                {job.company?.verified && <BadgeCheck size={14} className="text-[#1E3A5F]" />}
                {job.company?.rating > 0 && (
                  <>
                    <span>•</span>
                    <Star size={12} className="text-amber-500 fill-amber-500" />
                    <span>{job.company.rating}</span>
                  </>
                )}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {job.urgent && (
              <span className="px-2 py-1 bg-red-100 text-red-700 text-xs font-medium rounded-full flex items-center gap-1">
                <Zap size={12} />
                Urgent
              </span>
            )}
            <button
              onClick={() => {
                setSaved(!saved);
                onSave(job.id);
              }}
              className={`p-2 rounded-lg transition-colors ${
                saved ? 'text-[#F97316] bg-orange-50' : 'text-gray-400 hover:bg-gray-100'
              }`}
            >
              <Bookmark size={20} className={saved ? 'fill-current' : ''} />
            </button>
          </div>
        </div>

        {/* Badges */}
        <div className="flex flex-wrap gap-2 mb-3">
          <span className={`px-2 py-1 rounded-full text-xs font-medium ${badge.bg} ${badge.text}`}>
            {badge.label}
          </span>
          <span className="px-2 py-1 bg-gray-100 text-gray-600 rounded-full text-xs font-medium flex items-center gap-1">
            <MapPin size={12} />
            {job.location}
          </span>
          {job.salary && (
            <span className="px-2 py-1 bg-green-50 text-green-700 rounded-full text-xs font-medium flex items-center gap-1">
              <Euro size={12} />
              {job.salary}
            </span>
          )}
          <span className="px-2 py-1 bg-blue-50 text-blue-700 rounded-full text-xs font-medium">
            {job.experience_label}
          </span>
        </div>

        {/* Description courte */}
        <p className="text-gray-600 text-sm mb-4 line-clamp-2">{job.description}</p>

        {/* Skills */}
        {job.skills?.length > 0 && (
          <div className="flex flex-wrap gap-1 mb-4">
            {job.skills.slice(0, 4).map((skill, i) => (
              <span key={i} className="px-2 py-0.5 bg-[#1E3A5F]/10 text-[#1E3A5F] text-xs rounded-full">
                {skill}
              </span>
            ))}
            {job.skills.length > 4 && (
              <span className="px-2 py-0.5 bg-gray-100 text-gray-500 text-xs rounded-full">
                +{job.skills.length - 4}
              </span>
            )}
          </div>
        )}

        {/* Footer */}
        <div className="flex items-center justify-between pt-3 border-t border-gray-100">
          <div className="flex items-center gap-4 text-xs text-gray-500">
            <span className="flex items-center gap-1">
              <Eye size={14} />
              {job.views} vues
            </span>
            <span className="flex items-center gap-1">
              <Users size={14} />
              {job.applications_count} candidatures
            </span>
            <span className="flex items-center gap-1">
              <Clock size={14} />
              {job.time}
            </span>
          </div>
          <Button
            onClick={() => onApply(job)}
            size="sm"
            className="bg-[#F97316] hover:bg-[#ea6c0a] text-white"
          >
            <Send size={14} className="mr-1" />
            Postuler
          </Button>
        </div>
      </div>
    </motion.div>
  );
}

// Modal de candidature - Version mobile plein écran / Desktop centré
function ApplyModal({ isOpen, onClose, job, onSubmit }) {
  const [message, setMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isMobile] = useState(window.innerWidth < 768);
  const [keyboardHeight, setKeyboardHeight] = useState(0);

  // Détection du clavier et masquage navbar
  useEffect(() => {
    if (!isOpen || !isMobile) {
      setKeyboardHeight(0);
      return;
    }
    
    document.body.classList.add('modal-fullscreen-open');
    
    const viewport = window.visualViewport;
    if (!viewport) return;
    
    const handleViewportResize = () => {
      const heightDiff = window.innerHeight - viewport.height;
      setKeyboardHeight(heightDiff > 50 ? heightDiff : 0);
    };
    
    viewport.addEventListener('resize', handleViewportResize);
    viewport.addEventListener('scroll', handleViewportResize);
    
    return () => {
      document.body.classList.remove('modal-fullscreen-open');
      viewport.removeEventListener('resize', handleViewportResize);
      viewport.removeEventListener('scroll', handleViewportResize);
    };
  }, [isOpen, isMobile]);

  const handleSubmit = async () => {
    if (!message.trim()) {
      toast.error('Écris un message de motivation');
      return;
    }

    setIsLoading(true);
    try {
      await onSubmit({ job_id: job.id, message });
      onClose();
      setMessage('');
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen || !job) return null;

  // Version mobile plein écran
  if (isMobile) {
    return createPortal(
      <motion.div
        initial={{ y: '100%' }}
        animate={{ y: 0 }}
        exit={{ y: '100%' }}
        transition={{ duration: 0.25, ease: 'easeOut' }}
        className="fixed inset-0 z-[10002] flex flex-col bg-white"
      >
        {/* Header */}
        <header className="flex items-center gap-2 px-3 border-b border-gray-100"
          style={{ paddingTop: 'max(12px, env(safe-area-inset-top))', paddingBottom: '12px' }}>
          <button onClick={onClose} className="p-1 text-gray-500">
            <ArrowLeft size={22} />
          </button>
          <div>
            <h1 className="text-[15px] font-bold text-gray-900">Postuler</h1>
            <p className="text-xs text-gray-500">{job.title}</p>
          </div>
        </header>

        {/* Contenu scrollable */}
        <div className="flex-1 overflow-y-auto p-5">
          {/* Info entreprise */}
          <div className="flex items-center gap-3 mb-5 p-4 bg-gray-50 rounded-xl">
            <div className="w-12 h-12 rounded-lg bg-[#1E3A5F] flex items-center justify-center text-white font-bold">
              {job.company?.name?.slice(0, 2).toUpperCase()}
            </div>
            <div>
              <p className="font-semibold text-gray-900">{job.company?.name}</p>
              <p className="text-sm text-gray-500">{job.location}</p>
            </div>
          </div>

          {/* Message */}
          <div>
            <label className="block text-[12px] font-semibold mb-2 uppercase tracking-wide text-gray-500">Message de motivation *</label>
            <textarea
              value={message}
              onChange={e => setMessage(e.target.value)}
              placeholder="Présente-toi et explique pourquoi tu es intéressé par ce poste..."
              rows={8}
              className="w-full p-4 rounded-xl border text-[16px] bg-gray-50 border-gray-200 text-gray-900 placeholder-gray-400 resize-none"
            />
          </div>
        </div>

        {/* Footer fixe */}
        <div 
          className="p-4 border-t border-gray-100 bg-white transition-all duration-200"
          style={{ 
            paddingBottom: keyboardHeight > 0 ? '16px' : 'calc(16px + env(safe-area-inset-bottom, 0px))',
            marginBottom: keyboardHeight > 0 ? `${keyboardHeight}px` : '0px'
          }}
        >
          <button
            className="w-full flex items-center justify-center gap-2 px-4 py-2.5 mb-3 text-sm font-medium rounded-xl transition-all text-orange-600 bg-orange-50 hover:bg-orange-100 border border-orange-200"
          >
            <Sparkles size={16} />
            Créer avec Abracada
          </button>
          
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="flex-1 h-12 rounded-xl font-semibold transition-all border border-gray-300 text-[#1e3a5f] hover:bg-gray-100"
            >
              Annuler
            </button>
            <button
              onClick={handleSubmit}
              disabled={!message.trim() || isLoading}
              className="flex-1 h-12 bg-orange-500 hover:bg-orange-600 text-white rounded-xl font-semibold shadow-lg shadow-orange-500/25 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {isLoading ? (
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <>
                  <Send size={18} />
                  Envoyer
                </>
              )}
            </button>
          </div>
        </div>
      </motion.div>,
      document.body
    );
  }

  // Version desktop
  return createPortal(
    <div className="fixed inset-0 bg-black/60 z-[10002] flex items-center justify-center p-4" onClick={onClose}>
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-2xl w-full max-w-lg shadow-2xl overflow-hidden"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 bg-gradient-to-r from-[#1E3A5F] to-[#2d5a8a]">
          <div>
            <h2 className="font-semibold text-white">Postuler</h2>
            <p className="text-sm text-white/70">{job.title}</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-white/20 rounded-lg text-white">
            <X size={20} />
          </button>
        </div>

        <div className="p-6">
          <div className="flex items-center gap-3 mb-4 p-3 bg-gray-50 rounded-xl">
            <div className="w-10 h-10 rounded-lg bg-[#1E3A5F] flex items-center justify-center text-white font-bold text-sm">
              {job.company?.name?.slice(0, 2).toUpperCase()}
            </div>
            <div>
              <p className="font-medium text-gray-900">{job.company?.name}</p>
              <p className="text-sm text-gray-500">{job.location}</p>
            </div>
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">Message de motivation</label>
            <textarea
              value={message}
              onChange={e => setMessage(e.target.value)}
              placeholder="Présente-toi et explique pourquoi tu es intéressé par ce poste..."
              rows={5}
              className="w-full px-4 py-3 border border-gray-200 rounded-xl resize-none focus:outline-none focus:ring-2 focus:ring-[#1E3A5F]/20"
            />
          </div>

          <Button
            onClick={handleSubmit}
            disabled={!message.trim() || isLoading}
            className="w-full bg-[#F97316] hover:bg-[#ea6c0a] text-white py-3"
          >
            {isLoading ? (
              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : (
              <>
                <Send size={18} className="mr-2" />
                Envoyer ma candidature
              </>
            )}
          </Button>
        </div>
      </motion.div>
    </div>,
    document.body
  );
}

// Modal créer une offre - Version mobile plein écran / Desktop centré
function CreateJobModal({ isOpen, onClose, onSubmit }) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [jobType, setJobType] = useState('cdi');
  const [metier, setMetier] = useState('');
  const [location, setLocation] = useState('');
  const [salaryMin, setSalaryMin] = useState('');
  const [salaryMax, setSalaryMax] = useState('');
  const [experience, setExperience] = useState('debutant');
  const [skills, setSkills] = useState('');
  const [benefits, setBenefits] = useState('');
  const [urgent, setUrgent] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isMobile] = useState(window.innerWidth < 768);
  const [keyboardHeight, setKeyboardHeight] = useState(0);

  // Détection du clavier et masquage navbar
  useEffect(() => {
    if (!isOpen || !isMobile) {
      setKeyboardHeight(0);
      return;
    }
    
    document.body.classList.add('modal-fullscreen-open');
    
    const viewport = window.visualViewport;
    if (!viewport) return;
    
    const handleViewportResize = () => {
      const heightDiff = window.innerHeight - viewport.height;
      setKeyboardHeight(heightDiff > 50 ? heightDiff : 0);
    };
    
    viewport.addEventListener('resize', handleViewportResize);
    viewport.addEventListener('scroll', handleViewportResize);
    
    return () => {
      document.body.classList.remove('modal-fullscreen-open');
      viewport.removeEventListener('resize', handleViewportResize);
      viewport.removeEventListener('scroll', handleViewportResize);
    };
  }, [isOpen, isMobile]);

  const handleSubmit = async () => {
    if (!title.trim() || !description.trim() || !metier || !location.trim()) {
      toast.error('Remplis tous les champs obligatoires');
      return;
    }

    setIsLoading(true);
    try {
      await onSubmit({
        title,
        description,
        job_type: jobType,
        metier,
        location,
        salary_min: salaryMin ? parseInt(salaryMin) : null,
        salary_max: salaryMax ? parseInt(salaryMax) : null,
        salary_type: 'monthly',
        experience_required: experience,
        skills: skills ? skills.split(',').map(s => s.trim()) : [],
        benefits: benefits ? benefits.split(',').map(s => s.trim()) : [],
        urgent
      });
      onClose();
      // Reset
      setTitle(''); setDescription(''); setMetier(''); setLocation('');
      setSalaryMin(''); setSalaryMax(''); setSkills(''); setBenefits('');
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  // Version mobile plein écran
  if (isMobile) {
    return createPortal(
      <motion.div
        initial={{ y: '100%' }}
        animate={{ y: 0 }}
        exit={{ y: '100%' }}
        transition={{ duration: 0.25, ease: 'easeOut' }}
        className="fixed inset-0 z-[10002] flex flex-col bg-white"
      >
        {/* Header */}
        <header className="flex items-center gap-2 px-3 border-b border-gray-100"
          style={{ paddingTop: 'max(12px, env(safe-area-inset-top))', paddingBottom: '12px' }}>
          <button onClick={onClose} className="p-1 text-gray-500">
            <ArrowLeft size={22} />
          </button>
          <h1 className="text-[15px] font-bold text-gray-900">Nouvelle offre d'emploi</h1>
        </header>

        {/* Contenu scrollable */}
        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          <div>
            <label className="block text-[12px] font-semibold mb-2 uppercase tracking-wide text-gray-500">Titre du poste *</label>
            <input
              type="text"
              value={title}
              onChange={e => setTitle(e.target.value)}
              placeholder="Ex: Plombier expérimenté"
              className="w-full p-4 rounded-xl border text-[16px] bg-gray-50 border-gray-200 text-gray-900 placeholder-gray-400"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[12px] font-semibold mb-2 uppercase tracking-wide text-gray-500">Type de contrat *</label>
              <select
                value={jobType}
                onChange={e => setJobType(e.target.value)}
                className="w-full p-4 rounded-xl border text-[16px] bg-gray-50 border-gray-200 text-gray-900"
              >
                {JOB_TYPES.filter(t => t.id !== 'all').map(t => (
                  <option key={t.id} value={t.id}>{t.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-[12px] font-semibold mb-2 uppercase tracking-wide text-gray-500">Métier *</label>
              <select
                value={metier}
                onChange={e => setMetier(e.target.value)}
                className="w-full p-4 rounded-xl border text-[16px] bg-gray-50 border-gray-200 text-gray-900"
              >
                <option value="">Sélectionner...</option>
                {METIERS.map(m => (
                  <option key={m} value={m}>{m}</option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-[12px] font-semibold mb-2 uppercase tracking-wide text-gray-500">Localisation *</label>
            <input
              type="text"
              value={location}
              onChange={e => setLocation(e.target.value)}
              placeholder="Ex: Nice (06)"
              className="w-full p-4 rounded-xl border text-[16px] bg-gray-50 border-gray-200 text-gray-900 placeholder-gray-400"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[12px] font-semibold mb-2 uppercase tracking-wide text-gray-500">Salaire min (€/mois)</label>
              <input
                type="number"
                value={salaryMin}
                onChange={e => setSalaryMin(e.target.value)}
                placeholder="2200"
                className="w-full p-4 rounded-xl border text-[16px] bg-gray-50 border-gray-200 text-gray-900 placeholder-gray-400"
              />
            </div>
            <div>
              <label className="block text-[12px] font-semibold mb-2 uppercase tracking-wide text-gray-500">Salaire max (€/mois)</label>
              <input
                type="number"
                value={salaryMax}
                onChange={e => setSalaryMax(e.target.value)}
                placeholder="2800"
                className="w-full p-4 rounded-xl border text-[16px] bg-gray-50 border-gray-200 text-gray-900 placeholder-gray-400"
              />
            </div>
          </div>

          <div>
            <label className="block text-[12px] font-semibold mb-2 uppercase tracking-wide text-gray-500">Expérience requise</label>
            <select
              value={experience}
              onChange={e => setExperience(e.target.value)}
              className="w-full p-4 rounded-xl border text-[16px] bg-gray-50 border-gray-200 text-gray-900"
            >
              <option value="debutant">Débutant accepté</option>
              <option value="1-2ans">1-2 ans</option>
              <option value="3-5ans">3-5 ans</option>
              <option value="5+ans">5+ ans</option>
            </select>
          </div>

          <div>
            <label className="block text-[12px] font-semibold mb-2 uppercase tracking-wide text-gray-500">Description *</label>
            <textarea
              value={description}
              onChange={e => setDescription(e.target.value)}
              placeholder="Décris le poste, les missions, l'environnement de travail..."
              rows={4}
              className="w-full p-4 rounded-xl border text-[16px] bg-gray-50 border-gray-200 text-gray-900 placeholder-gray-400 resize-none"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[12px] font-semibold mb-2 uppercase tracking-wide text-gray-500">Compétences (séparées par ,)</label>
              <input
                type="text"
                value={skills}
                onChange={e => setSkills(e.target.value)}
                placeholder="Plomberie, Chauffage"
                className="w-full p-4 rounded-xl border text-[16px] bg-gray-50 border-gray-200 text-gray-900 placeholder-gray-400"
              />
            </div>
            <div>
              <label className="block text-[12px] font-semibold mb-2 uppercase tracking-wide text-gray-500">Avantages (séparés par ,)</label>
              <input
                type="text"
                value={benefits}
                onChange={e => setBenefits(e.target.value)}
                placeholder="Véhicule, Mutuelle"
                className="w-full p-4 rounded-xl border text-[16px] bg-gray-50 border-gray-200 text-gray-900 placeholder-gray-400"
              />
            </div>
          </div>

          <label className="flex items-center gap-2 cursor-pointer p-3 bg-gray-50 rounded-xl">
            <input
              type="checkbox"
              checked={urgent}
              onChange={e => setUrgent(e.target.checked)}
              className="w-5 h-5 rounded border-gray-300 text-[#F97316] focus:ring-[#F97316]"
            />
            <span className="text-sm text-gray-700 font-medium">Marquer comme urgent</span>
          </label>
        </div>

        {/* Footer fixe */}
        <div 
          className="p-4 border-t border-gray-100 bg-white transition-all duration-200"
          style={{ 
            paddingBottom: keyboardHeight > 0 ? '16px' : 'calc(16px + env(safe-area-inset-bottom, 0px))',
            marginBottom: keyboardHeight > 0 ? `${keyboardHeight}px` : '0px'
          }}
        >
          <button
            className="w-full flex items-center justify-center gap-2 px-4 py-2.5 mb-3 text-sm font-medium rounded-xl transition-all text-orange-600 bg-orange-50 hover:bg-orange-100 border border-orange-200"
          >
            <Sparkles size={16} />
            Créer avec Abracada
          </button>
          
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="flex-1 h-12 rounded-xl font-semibold transition-all border border-gray-300 text-[#1e3a5f] hover:bg-gray-100"
            >
              Annuler
            </button>
            <button
              onClick={handleSubmit}
              disabled={!title.trim() || !description.trim() || !metier || !location.trim() || isLoading}
              className="flex-1 h-12 bg-orange-500 hover:bg-orange-600 text-white rounded-xl font-semibold shadow-lg shadow-orange-500/25 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {isLoading ? (
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <>
                  <Briefcase size={18} />
                  Créer
                </>
              )}
            </button>
          </div>
        </div>
      </motion.div>,
      document.body
    );
  }

  // Version desktop
  return createPortal(
    <div className="fixed inset-0 bg-black/60 z-[10002] flex items-center justify-center p-4" onClick={onClose}>
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-2xl w-full max-w-2xl shadow-2xl overflow-hidden"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 bg-gradient-to-r from-[#1E3A5F] to-[#2d5a8a]">
          <h2 className="font-semibold text-white">Publier une offre d'emploi</h2>
          <button onClick={onClose} className="p-2 hover:bg-white/20 rounded-lg text-white">
            <X size={20} />
          </button>
        </div>

        <div className="p-6 max-h-[70vh] overflow-y-auto space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Titre du poste *</label>
            <input
              type="text"
              value={title}
              onChange={e => setTitle(e.target.value)}
              placeholder="Ex: Plombier expérimenté"
              className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#1E3A5F]/20"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Type de contrat *</label>
              <select
                value={jobType}
                onChange={e => setJobType(e.target.value)}
                className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#1E3A5F]/20"
              >
                {JOB_TYPES.filter(t => t.id !== 'all').map(t => (
                  <option key={t.id} value={t.id}>{t.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Métier *</label>
              <select
                value={metier}
                onChange={e => setMetier(e.target.value)}
                className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#1E3A5F]/20"
              >
                <option value="">Sélectionner...</option>
                {METIERS.map(m => (
                  <option key={m} value={m}>{m}</option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Localisation *</label>
            <input
              type="text"
              value={location}
              onChange={e => setLocation(e.target.value)}
              placeholder="Ex: Nice (06)"
              className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#1E3A5F]/20"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Salaire min (€/mois)</label>
              <input
                type="number"
                value={salaryMin}
                onChange={e => setSalaryMin(e.target.value)}
                placeholder="2200"
                className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#1E3A5F]/20"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Salaire max (€/mois)</label>
              <input
                type="number"
                value={salaryMax}
                onChange={e => setSalaryMax(e.target.value)}
                placeholder="2800"
                className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#1E3A5F]/20"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Expérience requise</label>
            <select
              value={experience}
              onChange={e => setExperience(e.target.value)}
              className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#1E3A5F]/20"
            >
              <option value="debutant">Débutant accepté</option>
              <option value="1-2ans">1-2 ans</option>
              <option value="3-5ans">3-5 ans</option>
              <option value="5+ans">5+ ans</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description *</label>
            <textarea
              value={description}
              onChange={e => setDescription(e.target.value)}
              placeholder="Décris le poste, les missions, l'environnement de travail..."
              rows={4}
              className="w-full px-4 py-2.5 border border-gray-200 rounded-xl resize-none focus:outline-none focus:ring-2 focus:ring-[#1E3A5F]/20"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Compétences (séparées par ,)</label>
              <input
                type="text"
                value={skills}
                onChange={e => setSkills(e.target.value)}
                placeholder="Plomberie, Chauffage, Autonomie"
                className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#1E3A5F]/20"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Avantages (séparés par ,)</label>
              <input
                type="text"
                value={benefits}
                onChange={e => setBenefits(e.target.value)}
                placeholder="Véhicule, Mutuelle, Primes"
                className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#1E3A5F]/20"
              />
            </div>
          </div>

          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={urgent}
              onChange={e => setUrgent(e.target.checked)}
              className="w-4 h-4 rounded border-gray-300 text-[#F97316] focus:ring-[#F97316]"
            />
            <span className="text-sm text-gray-700">Marquer comme urgent</span>
          </label>

          <Button
            onClick={handleSubmit}
            disabled={!title.trim() || !description.trim() || !metier || !location.trim() || isLoading}
            className="w-full bg-[#F97316] hover:bg-[#ea6c0a] text-white py-3"
          >
            {isLoading ? (
              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : (
              <>
                <Briefcase size={18} className="mr-2" />
                Publier l'offre
              </>
            )}
          </Button>
        </div>
      </motion.div>
    </div>,
    document.body
  );
}

// Page principale
export default function EmploiProPage() {
  const context = useOutletContext();
  const [jobs, setJobs] = useState([]);
  const [myJobs, setMyJobs] = useState([]);
  const [myCandidatures, setMyCandidatures] = useState([]);
  const [stats, setStats] = useState({});
  const [activeTab, setActiveTab] = useState('offres');
  const [activeFilter, setActiveFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showApplyModal, setShowApplyModal] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [savedJobs, setSavedJobs] = useState([]);
  
  // État pour le scroll des filtres mobile
  const [showScrollHint, setShowScrollHint] = useState(true);
  const filterScrollRef = useRef(null);
  
  const handleFilterScroll = () => {
    if (filterScrollRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = filterScrollRef.current;
      setShowScrollHint(scrollLeft < scrollWidth - clientWidth - 10);
    }
  };

  const token = context?.token || localStorage.getItem('client_token');

  // Charger les données
  useEffect(() => {
    loadJobs();
    loadStats();
  }, [activeFilter]);

  useEffect(() => {
    if (activeTab === 'mes-offres') loadMyJobs();
    if (activeTab === 'candidatures') loadMyCandidatures();
  }, [activeTab]);

  const loadJobs = async () => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams();
      if (activeFilter !== 'all') params.append('job_type', activeFilter);
      
      const response = await fetch(`${API_URL}/api/emploi-pro/offres?${params}`, {
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      });
      
      if (response.ok) {
        const data = await response.json();
        setJobs(data.jobs || []);
      }
    } catch (error) {
      console.error('Error loading jobs:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const loadMyJobs = async () => {
    try {
      const response = await fetch(`${API_URL}/api/emploi-pro/mes-offres`, {
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      });
      if (response.ok) {
        const data = await response.json();
        setMyJobs(data.jobs || []);
      }
    } catch (error) {
      console.error('Error loading my jobs:', error);
    }
  };

  const loadMyCandidatures = async () => {
    try {
      const response = await fetch(`${API_URL}/api/emploi-pro/mes-candidatures`, {
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      });
      if (response.ok) {
        const data = await response.json();
        setMyCandidatures(data.candidatures || []);
      }
    } catch (error) {
      console.error('Error loading candidatures:', error);
    }
  };

  const loadStats = async () => {
    try {
      const response = await fetch(`${API_URL}/api/emploi-pro/stats`, {
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      });
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  };

  // Seeder les données démo
  useEffect(() => {
    const seedDemo = async () => {
      try {
        await fetch(`${API_URL}/api/emploi-pro/seed-demo`, { method: 'POST' });
        loadJobs();
      } catch (error) {
        console.error('Error seeding:', error);
      }
    };

    if (jobs.length === 0 && !isLoading) {
      seedDemo();
    }
  }, [jobs.length, isLoading]);

  // Handlers
  const handleApply = (job) => {
    setSelectedJob(job);
    setShowApplyModal(true);
  };

  const handleSubmitCandidature = async (data) => {
    try {
      const response = await fetch(`${API_URL}/api/emploi-pro/candidatures`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        },
        body: JSON.stringify(data)
      });
      
      if (response.ok) {
        toast.success('Candidature envoyée !');
        loadMyCandidatures();
      } else {
        throw new Error();
      }
    } catch (error) {
      toast.error('Erreur lors de l\'envoi');
    }
  };

  const handleCreateJob = async (data) => {
    try {
      const response = await fetch(`${API_URL}/api/emploi-pro/offres`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        },
        body: JSON.stringify(data)
      });
      
      if (response.ok) {
        toast.success('Offre publiée !');
        loadJobs();
        loadMyJobs();
        loadStats();
      } else {
        throw new Error();
      }
    } catch (error) {
      toast.error('Erreur lors de la publication');
    }
  };

  const handleSaveJob = (jobId) => {
    if (savedJobs.includes(jobId)) {
      setSavedJobs(prev => prev.filter(id => id !== jobId));
      toast.success('Offre retirée des favoris');
    } else {
      setSavedJobs(prev => [...prev, jobId]);
      toast.success('Offre sauvegardée !');
    }
  };

  // Filtrer les jobs par recherche
  const filteredJobs = jobs.filter(job => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      job.title?.toLowerCase().includes(query) ||
      job.company?.name?.toLowerCase().includes(query) ||
      job.location?.toLowerCase().includes(query) ||
      job.metier?.toLowerCase().includes(query)
    );
  });

  return (
    <div className="max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Briefcase size={24} className="text-[#1E3A5F]" />
            Emploi Pro
          </h1>
          <p className="text-gray-500 text-sm mt-1">
            Trouve ton prochain poste ou recrute des talents
          </p>
        </div>
        <Button
          onClick={() => setShowCreateModal(true)}
          className="bg-[#F97316] hover:bg-[#ea6c0a] text-white"
        >
          <PlusCircle size={18} className="mr-2" />
          Publier une offre
        </Button>
      </div>

      {/* Quick stats */}
      <div className="grid grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-xl p-4 border border-gray-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
              <Briefcase size={20} className="text-[#1E3A5F]" />
            </div>
            <div>
              <p className="text-xs text-gray-500">Mes offres</p>
              <p className="font-bold text-gray-900">{stats.my_offers || 0}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 border border-gray-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
              <Eye size={20} className="text-green-600" />
            </div>
            <div>
              <p className="text-xs text-gray-500">Vues totales</p>
              <p className="font-bold text-gray-900">{stats.total_views || 0}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 border border-gray-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-orange-100 flex items-center justify-center">
              <Users size={20} className="text-[#F97316]" />
            </div>
            <div>
              <p className="text-xs text-gray-500">Candidatures reçues</p>
              <p className="font-bold text-gray-900">{stats.total_applications || 0}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 border border-gray-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
              <Send size={20} className="text-purple-600" />
            </div>
            <div>
              <p className="text-xs text-gray-500">Mes candidatures</p>
              <p className="font-bold text-gray-900">{stats.my_candidatures || 0}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="mb-6">
        <TabsList className="grid w-full grid-cols-3 bg-gray-100 rounded-xl p-1">
          <TabsTrigger value="offres" className="rounded-lg data-[state=active]:bg-white">
            <Search size={16} className="mr-2" />
            Rechercher
          </TabsTrigger>
          <TabsTrigger value="mes-offres" className="rounded-lg data-[state=active]:bg-white">
            <Briefcase size={16} className="mr-2" />
            Mes offres
          </TabsTrigger>
          <TabsTrigger value="candidatures" className="rounded-lg data-[state=active]:bg-white">
            <FileText size={16} className="mr-2" />
            Candidatures
          </TabsTrigger>
        </TabsList>

        {/* Tab: Rechercher */}
        <TabsContent value="offres" className="mt-6">
          {/* Search + Filters */}
          <div className="flex gap-4 mb-6">
            <div className="flex-1 relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                placeholder="Rechercher un poste, une entreprise..."
                className="w-full pl-12 pr-4 py-3 bg-white border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#1E3A5F]/20"
              />
            </div>
          </div>

          {/* Type filters - Style premium avec scroll horizontal */}
          <div className="mb-6">
            {/* Mobile */}
            <div className="sm:hidden">
              <div className="relative flex items-center">
                <div 
                  ref={filterScrollRef}
                  onScroll={handleFilterScroll}
                  className="flex gap-1.5 overflow-x-auto scrollbar-hide flex-1 pr-6"
                  style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
                >
                  {JOB_TYPES.map(type => (
                    <button
                      key={type.id}
                      onClick={() => setActiveFilter(type.id)}
                      data-testid={`filter-${type.id}`}
                      className={`flex-shrink-0 px-3 py-2 rounded-xl text-xs font-semibold transition-all whitespace-nowrap flex items-center gap-1.5 ${
                        activeFilter === type.id
                          ? 'bg-orange-500 text-white shadow-sm'
                          : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-200'
                      }`}
                    >
                      {type.label}
                    </button>
                  ))}
                </div>
                
                {/* Flèche orange discrète */}
                {showScrollHint && (
                  <div className="flex-shrink-0 flex items-center pointer-events-none">
                    <ChevronRight size={18} className="text-orange-500" strokeWidth={2.5} />
                  </div>
                )}
              </div>
            </div>
            
            {/* Desktop */}
            <div className="hidden sm:flex flex-wrap gap-2">
              {JOB_TYPES.map(type => (
                <button
                  key={type.id}
                  onClick={() => setActiveFilter(type.id)}
                  className={`px-4 py-2 rounded-xl text-sm font-medium transition-colors flex items-center gap-2 ${
                    activeFilter === type.id
                      ? 'bg-orange-500 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {type.label}
                </button>
              ))}
            </div>
          </div>

          {/* Jobs list */}
          {isLoading ? (
            <div className="flex justify-center py-12">
              <div className="w-8 h-8 border-4 border-[#1E3A5F] border-t-transparent rounded-full animate-spin" />
            </div>
          ) : filteredJobs.length === 0 ? (
            <div className="text-center py-12 bg-white rounded-2xl border border-gray-100">
              <Briefcase size={48} className="text-gray-300 mx-auto mb-4" />
              <h3 className="font-semibold text-gray-700 mb-2">Aucune offre trouvée</h3>
              <p className="text-gray-500 text-sm">Modifie tes critères de recherche</p>
            </div>
          ) : (
            <div className="space-y-4">
              {filteredJobs.map(job => (
                <JobCard
                  key={job.id}
                  job={job}
                  onApply={handleApply}
                  onSave={handleSaveJob}
                  isSaved={savedJobs.includes(job.id)}
                />
              ))}
            </div>
          )}
        </TabsContent>

        {/* Tab: Mes offres */}
        <TabsContent value="mes-offres" className="mt-6">
          {myJobs.length === 0 ? (
            <div className="text-center py-12 bg-white rounded-2xl border border-gray-100">
              <Briefcase size={48} className="text-gray-300 mx-auto mb-4" />
              <h3 className="font-semibold text-gray-700 mb-2">Aucune offre publiée</h3>
              <p className="text-gray-500 text-sm mb-4">Publie ta première offre d'emploi</p>
              <Button onClick={() => setShowCreateModal(true)} className="bg-[#F97316]">
                <PlusCircle size={18} className="mr-2" />
                Publier une offre
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              {myJobs.map(job => (
                <JobCard
                  key={job.id}
                  job={job}
                  onApply={() => {}}
                  onSave={() => {}}
                  isSaved={false}
                />
              ))}
            </div>
          )}
        </TabsContent>

        {/* Tab: Candidatures */}
        <TabsContent value="candidatures" className="mt-6">
          {myCandidatures.length === 0 ? (
            <div className="text-center py-12 bg-white rounded-2xl border border-gray-100">
              <FileText size={48} className="text-gray-300 mx-auto mb-4" />
              <h3 className="font-semibold text-gray-700 mb-2">Aucune candidature</h3>
              <p className="text-gray-500 text-sm">Tu n'as pas encore postulé à une offre</p>
            </div>
          ) : (
            <div className="space-y-3">
              {myCandidatures.map(candidature => (
                <div key={candidature.id} className="bg-white rounded-xl p-4 border border-gray-100">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="font-medium text-gray-900">{candidature.job_title}</h4>
                      <p className="text-sm text-gray-500">{candidature.company_name}</p>
                    </div>
                    <div className="text-right">
                      <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${
                        candidature.status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                        candidature.status === 'viewed' ? 'bg-blue-100 text-blue-700' :
                        candidature.status === 'shortlisted' ? 'bg-green-100 text-green-700' :
                        candidature.status === 'rejected' ? 'bg-red-100 text-red-700' :
                        candidature.status === 'hired' ? 'bg-emerald-100 text-emerald-700' :
                        'bg-gray-100 text-gray-700'
                      }`}>
                        {candidature.status_label}
                      </span>
                      <p className="text-xs text-gray-400 mt-1">{candidature.time}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Modals */}
      <CreateJobModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSubmit={handleCreateJob}
      />

      <ApplyModal
        isOpen={showApplyModal}
        onClose={() => setShowApplyModal(false)}
        job={selectedJob}
        onSubmit={handleSubmitCandidature}
      />
    </div>
  );
}
